import type { FC, ReactNode } from 'react'
import type { Profile } from '@/types'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { buttonVariants } from '@/components/ui/button'
import { NAV_LINKS, siteConfig } from '@/constants/site'

interface SiteHeaderProps {
    profile: Profile | null
}

const isActive = (pathname: string, href: string): boolean => {
    if (href.includes('#')) return false

    return pathname === href || pathname.startsWith(`${href}/`)
}

export const SiteHeader: FC<SiteHeaderProps> = ({ profile }): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const closeMenu = () => setMenuOpen(false)

        router.events.on('routeChangeStart', closeMenu)
        router.events.on('hashChangeStart', closeMenu)

        return () => {
            router.events.off('routeChangeStart', closeMenu)
            router.events.off('hashChangeStart', closeMenu)
        }
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [menuOpen])

    return (
        <header
            id='site-header'
            className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md'
        >
            <Container className='flex h-16 items-center justify-between gap-6'>
                <Link
                    href='/'
                    className='group flex items-center gap-2.5 rounded-full text-[0.95rem] font-medium tracking-tight outline-none focus-visible:ring-3 focus-visible:ring-ring/50'
                >
                    <span
                        aria-hidden='true'
                        className='size-2.5 rounded-full bg-brand transition-transform duration-300 group-hover:scale-125'
                    />

                    {profile
                        ? <span data-mdlfy-cms={`profile:${profile.id}:name`}>{profile.name}</span>
                        : <span>{siteConfig.name}</span>}
                </Link>

                <nav aria-label='Main' className='hidden items-center gap-1 sm:flex'>
                    {NAV_LINKS.map(link => {
                        const active = isActive(router.pathname, link.href)

                        if (link.href === '/contact') {
                            return (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(buttonVariants({ size: 'lg' }), 'ml-3 rounded-full px-4')}
                                >
                                    {link.label}
                                </Link>
                            )
                        }

                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'rounded-full px-3.5 py-2 text-sm text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                                    active && 'text-foreground'
                                )}
                            >
                                {link.label}
                            </Link>
                        )
                    })}
                </nav>

                <button
                    type='button'
                    aria-expanded={menuOpen}
                    aria-controls='mobile-menu'
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    onClick={() => setMenuOpen(open => !open)}
                    className='inline-flex size-10 items-center justify-center rounded-full border border-border transition-colors outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 sm:hidden'
                >
                    {menuOpen ? <X className='size-5' /> : <Menu className='size-5' />}
                </button>
            </Container>

            <div
                id='mobile-menu'
                hidden={!menuOpen}
                className='border-t border-border/70 bg-background sm:hidden motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200'
            >
                <Container className='pb-6 pt-2'>
                    <nav aria-label='Mobile'>
                        <ul>
                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        onClick={() => setMenuOpen(false)}
                                        aria-current={isActive(router.pathname, link.href) ? 'page' : undefined}
                                        className='flex items-center justify-between border-b border-border/60 py-4 font-heading text-3xl outline-none focus-visible:text-brand'
                                    >
                                        {link.label}

                                        <ArrowRight className='size-5 text-muted-foreground' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </Container>
            </div>
        </header>
    )
}
